import Tippy, { TippyProps } from '@tippyjs/react';
import _ from 'lodash';
import React from 'react';
import 'tippy.js/animations/shift-away.css';

interface IProps {
  children?: React.ReactElement;
  closeFromContent?: boolean;
  contentClassName?: string;
  title?: string;
  type: 'click' | 'hover';
  onClick?: () => void;
  externalRef?: React.MutableRefObject<any>;
}

const PopoverDialog = (props: IProps & TippyProps) => {
  const popoverRef = React.useRef<any>(null);

  const {
    children,
    content,
    closeFromContent,
    title,
    type,
    className,
    onClick,
    externalRef,
  } = props;

  const [isVisible, setIsVisible] = React.useState(false);

  const show = () => setIsVisible(true);
  const hide = () => setIsVisible(false);

  const hideOnContentClick = () => {
    if (popoverRef.current) {
      popoverRef.current.hide();
    }
  };

  const contentTitle = !!title && <div className="popover-title">{title}</div>;
  const contentItem =
    closeFromContent && React.isValidElement(content)
      ? React.cloneElement((<div>{content}</div>) as React.ReactElement, {
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            type === 'hover' ? hideOnContentClick() : hide();
            if (content.props.onClick) content.props.onClick(e);
          },
        })
      : content;

  const childrenItem =
    type === 'click'
      ? React.cloneElement(children as React.ReactElement, {
          onClick: () => (isVisible ? hide() : show()),
        })
      : children;

  React.useEffect(() => {
    if (externalRef) {
      externalRef.current = popoverRef.current;
    }
  }, [externalRef, popoverRef]);

  return (
    <div onClick={onClick} className="dropdown-element">
      <Tippy
        onCreate={instance => (popoverRef.current = instance)}
        popperOptions={{
          modifiers: [
            {
              name: 'fitTooltipToViewport',
              enabled: true,
              phase: 'beforeWrite',
              fn: ({ state }) => {
                if (window.innerWidth > 992) return;

                const viewportWidth = document.documentElement.clientWidth;
                const reference = state.rects.reference;
                const placement = state.placement.split('-')[0];
                let horizontalSpace = viewportWidth;
                if (placement === 'right') {
                  horizontalSpace =
                    viewportWidth - (reference.x + reference.width);
                } else if (placement === 'left') {
                  horizontalSpace = reference.x;
                }

                state.styles.popper.maxWidth = `${Math.max(
                  0,
                  horizontalSpace - 24,
                )}px`;
              },
            },
          ],
        }}
        {..._.omit(props, [
          'title',
          'type',
          'trigger',
          'children',
          'visible',
          'content',
          'onClick',
          'onClickOutside',
          'closeFromContent',
          'className',
          'useCartRef',
          'externalRef',
        ])}
        className={`dropdown-menu ${className}`.trim()}
        visible={type === 'click' ? isVisible : undefined}
        content={
          <>
            {contentTitle}
            {contentItem}
          </>
        }
        onClickOutside={hide}
        onMount={instance => {
          props.onMount?.(instance);
          requestAnimationFrame(() => {
            instance.popperInstance?.update();
          });
        }}
      >
        {childrenItem}
      </Tippy>
    </div>
  );
};

PopoverDialog.defaultProps = {
  interactive: true,
  animation: 'shift-away',
};

export default PopoverDialog;
